import mapboxgl from 'mapbox-gl';
import html2canvas from 'html2canvas';

/**
 * Service for creating map visualizations for PDF generation
 * Creates temporary map instances to capture route visualizations
 */
class MapPDFService {
  constructor() {
    this.tempMap = null;
    this.tempContainer = null;
  }

  /**
   * Create a temporary map container for PDF capture
   */
  createTempMapContainer() {
    // Create a temporary container that's hidden from view
    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      width: 800px;
      height: 600px;
      background: white;
      z-index: -1;
    `;
    container.id = 'temp-map-pdf-' + Date.now();
    document.body.appendChild(container);
    
    this.tempContainer = container;
    return container;
  }

  /**
   * Initialize a temporary map for PDF generation
   */
  async initializeTempMap(accessToken) {
    if (!accessToken) {
      console.error('Mapbox access token required for PDF map generation');
      return null;
    }

    try {
      const container = this.createTempMapContainer();
      
      mapboxgl.accessToken = accessToken;
      this.tempMap = new mapboxgl.Map({
        container: container,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [28.9784, 41.0082], // Default center (Istanbul)
        zoom: 10,
        preserveDrawingBuffer: true, // Important for capturing
        attributionControl: false
      });

      // Wait for map to load
      await new Promise((resolve, reject) => {
        this.tempMap.on('load', resolve);
        this.tempMap.on('error', reject);
        setTimeout(() => reject(new Error('Map load timeout')), 10000);
      });

      // Add sources for routes and markers
      this.tempMap.addSource('routes', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      return this.tempMap;
    } catch (error) {
      console.error('Error initializing temp map:', error);
      this.cleanup();
      return null;
    }
  }

  /**
   * Generate VRP/TSP route visualization
   */
  async generateRouteVisualization(routeData) {
    if (!this.tempMap || !routeData.routes || !routeData.allLocations) {
      return null;
    }

    try {
      // Calculate bounds for all locations
      const bounds = new mapboxgl.LngLatBounds();
      routeData.allLocations.forEach(location => {
        bounds.extend(location);
      });

      // Fit map to bounds
      this.tempMap.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 15
      });

      // Wait for map to settle
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Add route lines
      const routeFeatures = [];
      const routeColors = this.generateRouteColors(routeData.routes.length);

      for (let routeIndex = 0; routeIndex < routeData.routes.length; routeIndex++) {
        const route = routeData.routes[routeIndex];
        const color = routeColors[routeIndex];

        // Create route features (simplified - straight lines between points)
        for (let i = 0; i < route.stops.length - 1; i++) {
          const startPoint = routeData.allLocations[route.stops[i]];
          const endPoint = routeData.allLocations[route.stops[i + 1]];

          routeFeatures.push({
            type: 'Feature',
            properties: {
              routeId: routeIndex,
              color: color
            },
            geometry: {
              type: 'LineString',
              coordinates: [startPoint, endPoint]
            }
          });
        }
      }

      // Update route source
      this.tempMap.getSource('routes').setData({
        type: 'FeatureCollection',
        features: routeFeatures
      });

      // Add route layers
      routeData.routes.forEach((route, routeIndex) => {
        const color = routeColors[routeIndex];
        const layerId = `route-${routeIndex}`;

        if (!this.tempMap.getLayer(layerId)) {
          this.tempMap.addLayer({
            id: layerId,
            type: 'line',
            source: 'routes',
            filter: ['==', 'routeId', routeIndex],
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': color,
              'line-width': 4,
              'line-opacity': 0.8
            }
          });
        }
      });

      // Add location markers
      routeData.allLocations.forEach((location, index) => {
        const isDepot = index === 0;
        new mapboxgl.Marker({
          color: isDepot ? '#e74c3c' : '#3498db',
          scale: isDepot ? 1.2 : 1
        })
        .setLngLat(location)
        .addTo(this.tempMap);
      });

      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 2000));

      return this.tempContainer;
    } catch (error) {
      console.error('Error generating route visualization:', error);
      return null;
    }
  }

  /**
   * Generate LAP allocation visualization
   */
  async generateLAPVisualization(routeData) {
    if (!this.tempMap || !routeData.facilities || !routeData.demands || !routeData.assignments) {
      return null;
    }

    try {
      // Calculate bounds for all locations
      const bounds = new mapboxgl.LngLatBounds();
      routeData.facilities.forEach(facility => bounds.extend(facility.coordinates));
      routeData.demands.forEach(demand => bounds.extend(demand.coordinates));

      // Fit map to bounds
      this.tempMap.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 15
      });

      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create allocation lines
      const allocationFeatures = [];
      const facilityColors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];

      routeData.assignments.forEach((assignment) => {
        const facility = routeData.facilities[assignment.facility_idx];
        const demand = routeData.demands[assignment.demand_idx];
        const color = facilityColors[assignment.facility_idx % facilityColors.length];

        allocationFeatures.push({
          type: 'Feature',
          properties: {
            facilityIdx: assignment.facility_idx,
            color: color
          },
          geometry: {
            type: 'LineString',
            coordinates: [facility.coordinates, demand.coordinates]
          }
        });
      });

      // Update routes source with allocation lines
      this.tempMap.getSource('routes').setData({
        type: 'FeatureCollection',
        features: allocationFeatures
      });

      // Add allocation lines layer
      if (!this.tempMap.getLayer('allocations')) {
        this.tempMap.addLayer({
          id: 'allocations',
          type: 'line',
          source: 'routes',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 2,
            'line-opacity': 0.7
          }
        });
      }

      // Add facility markers
      routeData.facilities.forEach((facility, index) => {
        const color = facilityColors[index % facilityColors.length];
        new mapboxgl.Marker({
          color: color,
          scale: 1.5
        })
        .setLngLat(facility.coordinates)
        .addTo(this.tempMap);
      });

      // Add demand markers
      routeData.demands.forEach((demand) => {
        new mapboxgl.Marker({
          color: '#95a5a6',
          scale: 0.8
        })
        .setLngLat(demand.coordinates)
        .addTo(this.tempMap);
      });

      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 2000));

      return this.tempContainer;
    } catch (error) {
      console.error('Error generating LAP visualization:', error);
      return null;
    }
  }

  /**
   * Capture the map as an image
   */
  async captureMapImage() {
    if (!this.tempContainer) {
      return null;
    }

    try {
      const canvas = await html2canvas(this.tempContainer, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: 800,
        height: 600
      });

      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error capturing map image:', error);
      return null;
    }
  }

  /**
   * Generate route colors
   */
  generateRouteColors(numRoutes) {
    const colors = [
      '#4285F4', '#EA4335', '#FBBC05', '#34A853', '#FF6D01', 
      '#46BDC6', '#7C4DFF', '#1B9E77', '#D95F02', '#7570B3',
      '#E7298A', '#66A61E', '#E6AB02', '#A6761D', '#666666'
    ];
    
    return Array(numRoutes).fill().map((_, i) => colors[i % colors.length]);
  }

  /**
   * Clean up temporary resources
   */
  cleanup() {
    if (this.tempMap) {
      this.tempMap.remove();
      this.tempMap = null;
    }
    
    if (this.tempContainer && this.tempContainer.parentNode) {
      this.tempContainer.parentNode.removeChild(this.tempContainer);
      this.tempContainer = null;
    }
  }

  /**
   * Generate map image for PDF based on solver type
   */
  async generateMapForPDF(routeData, solverType) {
    const accessToken = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;
    
    try {
      await this.initializeTempMap(accessToken);
      
      if (!this.tempMap) {
        return null;
      }

      let container = null;
      
      if (solverType === 'LAP') {
        container = await this.generateLAPVisualization(routeData);
      } else {
        container = await this.generateRouteVisualization(routeData, solverType);
      }

      if (container) {
        const imageData = await this.captureMapImage();
        return imageData;
      }

      return null;
    } catch (error) {
      console.error('Error generating map for PDF:', error);
      return null;
    } finally {
      this.cleanup();
    }
  }
}

export default new MapPDFService();